import {
  selectShowtimeDomain,
  useGetCurrentUserQuery,
  useGetOrganizationQuery,
} from '@carboncopyco/data';
import { InfoOutlineIcon } from '@carboncopyco/icons';
import {
  ShareSecurityLevel,
  type TourAspectRatio,
  type TourShare,
  type WelcomeMatConfig,
} from '@carboncopyco/types';
import {
  Button,
  Checkbox,
  ColorHex,
  InputTextField,
  LoadingDots,
  Modal,
  type ModalProps,
  SubmitButton,
  TextBase,
  TextCaption,
  TextSubHeader,
  TooltipBubble,
} from '@carboncopyco/ui';
import { useBoolean } from 'ahooks';
import {
  Field,
  type FieldProps,
  Form,
  Formik,
  type FormikHelpers,
} from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useMemo, useState } from 'react';
import { array, boolean, number, object, string } from 'yup';
import { useIntegration } from '../Integrations';
import { EMAIL_DOMAIN_REGEXP, ValidationErrorMessages } from '../consts';
import { AdvancedSettings } from './AdvancedSettings';
import { ExpirationSection } from './ExpirationSection';
import { FilterEmailsField } from './FilterEmailsField';
import { FormPreviewModal } from './FormPreviewModal';
import { IntegrationLeadsField } from './IntegrationLeadsField';
import { SecurityField } from './SecurityField';
import {
  type IShareDropdownItem,
  ShareDropdownMenu,
} from './ShareDropdownMenu';
import { SlackIntegrationField } from './SlackIntegrationField';
import { WelcomeMatField } from './WelcomeMatField';
import {
  WELCOME_MAT_TITLE_MAX_LENGTH,
  WelcomeMatTitleField,
} from './WelcomeMatTitleField';
import {
  type SecurityItem,
  getAccessLevels,
  tourAspectRatioItems,
} from './consts';
import { useAllowedShareSecurityLevels } from './useAllowedShareSecurityLevels';
import { useSelector } from 'react-redux';

export type ShareTourModalSecurity = Omit<
  TourShare['options']['security'],
  'level'
> & {
  level: ShareSecurityLevel;
};

export type ShareTourModalState = {
  linkDescription: TourShare['options']['linkDescription'];
  isActive: TourShare['isActive'];
  skipWelcome: TourShare['options']['skipWelcome'];
  expiresAt: TourShare['options']['expiresAt'];
  daysToExpire: TourShare['options']['daysToExpire'];
  security: ShareTourModalSecurity;
  toggleExpiration: boolean;
  autoSyncUpdates: TourShare['options']['autoSyncUpdates'];
  tourAspectRatio?: TourAspectRatio;
  cover: WelcomeMatConfig;
  salesforceLeads: boolean;
  hubspotLeads: boolean;
  slackIntegration: boolean;
  welcomeMatTitle: string;
};

export type ShareTourModalProps = Required<
  Pick<ModalProps, 'title' | 'open' | 'onClose'>
> & {
  organizationId: string;
  toursShareAspectRatioEnabled: boolean;
  initialValues: ShareTourModalState;
  handleSubmit: (
    values: ShareTourModalState,
    formikHelpers: FormikHelpers<ShareTourModalState>
  ) => void;
  isLoading?: boolean;
  yesLabel?: string;
};

export const ShareTourModal = ({
  organizationId,
  toursShareAspectRatioEnabled,
  open,
  onClose,
  title,
  initialValues,
  handleSubmit,
  isLoading,
  yesLabel = 'Create link',
}: ShareTourModalProps) => {
  const flags = useFlags();
  const showtimeDomain = useSelector(selectShowtimeDomain);
  const shareTourFormSchema = useMemo(
    () =>
      object({
        linkDescription: string()
          .required(ValidationErrorMessages.required)
          .max(200, 'Link description must be at most 200 characters'),
        skipWelcome: boolean(),
        security: object({
          level: string(),
          emailDomain: string().when('level', {
            is: 'email_domain',
            then: (schema) =>
              schema
                .required(ValidationErrorMessages.required)
                .matches(
                  EMAIL_DOMAIN_REGEXP,
                  ValidationErrorMessages.invalidEmailDomain
                ),
          }),
          emails: array().when('level', {
            is: 'restricted',
            then: (schema) =>
              schema
                .required(ValidationErrorMessages.required)
                .of(string().email('Invalid email'))
                .min(1, 'Please fill out at least one email address '),
          }),
        }),
        isActive: boolean(),
        daysToExpire: number()
          .nullable()
          .min(1, 'Expiration date should be one day or more from today')
          .max(999, 'Expiration date must be within 1,000 days'),
        syncUpdates: boolean(),
        tourAspectRatio: string(),
        welcomeMatTitle: flags.welcomeMatTitle
          ? string()
              .required(ValidationErrorMessages.required)
              .max(
                WELCOME_MAT_TITLE_MAX_LENGTH,
                `Exceeds max length of ${WELCOME_MAT_TITLE_MAX_LENGTH} characters`
              )
          : undefined,
      }),
    [flags]
  );
  const { data: organization } = useGetOrganizationQuery({
    id: organizationId,
  });
  const { data: userResponse } = useGetCurrentUserQuery();
  const { user } = userResponse || {};
  const [showFormPreviewModal, setShowFormPreviewModal] = useBoolean(false);
  const [selectedTourAspectRatio, setSelectedTourAspectRatio] =
    useState<IShareDropdownItem>(
      flags?.tourCreateShareDefaultRatioFixed
        ? tourAspectRatioItems[1]
        : tourAspectRatioItems.find(
            (item) => item.id === initialValues.tourAspectRatio
          ) || tourAspectRatioItems[0]
    );

  const { disabled } = useIntegration({
    integrationType: 'slack',
    organizationId,
  });

  const slackIntegrationInitialState = disabled
    ? false
    : !flags.slackIntegrationControl;

  const handleSecurityLevelChange = useCallback(
    (level: ShareSecurityLevel, values) => {
      const { security, client, ...restValues } = values;
      const newValues = {
        ...restValues,
        security: { level },
      };
      switch (level) {
        case 'internal':
          newValues.skipWelcome = false;
          newValues.client = 'internal';
          newValues.security.emailDomain = user?.email.split('@').slice(-1)[0];
          newValues.salesforceLeads = false;
          newValues.hubspotLeads = false;
          newValues.slackIntegration = false;
          break;
        case 'email_domain':
          newValues.skipWelcome = false;
          newValues.slackIntegration = slackIntegrationInitialState;
          break;
        case 'restricted':
          newValues.skipWelcome = false;
          newValues.slackIntegration = slackIntegrationInitialState;
          break;
        case 'anyone':
          newValues.client = 'anyone';
          newValues.slackIntegration = slackIntegrationInitialState;
          newValues.security.filterEmailDomains = !!security.filterEmailDomains;
          break;
      }

      return newValues;
    },
    [user, slackIntegrationInitialState]
  );

  const { allowedTourSecurityLevels } =
    useAllowedShareSecurityLevels(organizationId);
  const accessLevels = useMemo(getAccessLevels, []);
  const items: SecurityItem[] = Object.values(ShareSecurityLevel).map(
    (level) => ({
      ...accessLevels[level],
      disabled: !allowedTourSecurityLevels.includes(level),
    })
  );

  return (
    <Modal
      open={open}
      title={
        <div className="flex items-center">
          <TextSubHeader
            label={title}
            color="text-ds-natural-600"
          />
          <div className="ml-2 cursor-pointer">
            <TooltipBubble
              title="Setting will only apply on this share link"
              placement="top-start"
            >
              <InfoOutlineIcon
                color={ColorHex.natural300}
                size="18"
              />
            </TooltipBubble>
          </div>
        </div>
      }
      onClose={onClose}
    >
      <Formik<ShareTourModalState>
        validationSchema={shareTourFormSchema}
        onSubmit={(values, formikHelpers) =>
          handleSubmit(values, formikHelpers)
        }
        initialValues={initialValues}
      >
        {({ values }) => (
          <Form>
            <div className="flex flex-col gap-2 w-[600px] mt-3">
              {flags.welcomeMatTitle && <WelcomeMatTitleField />}
              <div className="gap-2">
                <InputTextField
                  label="Link name"
                  required
                  name="linkDescription"
                  validateFieldOnBlur
                  clearOnFocus
                />
                <div className="flex flex-col gap-5">
                  <SecurityField
                    items={items}
                    onSecurityLevelChange={handleSecurityLevelChange}
                  />
                  {toursShareAspectRatioEnabled && (
                    <Field name="tourAspectRatio">
                      {({ form }: FieldProps) => {
                        return (
                          <ShareDropdownMenu
                            selectedItem={selectedTourAspectRatio}
                            items={tourAspectRatioItems}
                            onSelect={(item) => {
                              setSelectedTourAspectRatio(item);
                              form.setFieldValue('tourAspectRatio', item.id);
                            }}
                          />
                        );
                      }}
                    </Field>
                  )}
                  {flags.filterEmailDomains &&
                    values.security.level === 'anyone' && <FilterEmailsField />}
                </div>
                <AdvancedSettings mountWhenHidden>
                  <div className="flex flex-col">
                    <ExpirationSection />
                    <IntegrationLeadsField
                      vendor="salesforce"
                      securityLevel={values.security.level}
                      infoText="Every visitor will become a lead in Salesforce. Your Salesforce Admin can configure SFDC to convert that Lead into a Contact and add them as a Contact role on your open Opp."
                    />
                    {flags?.hubspotIntegration && (
                      <IntegrationLeadsField
                        securityLevel={values.security.level}
                        vendor="hubspot"
                        infoText="Every visitor will become a Contact in HubSpot. Get a detailed view of who is engaging with your product"
                      />
                    )}
                    {flags?.slackIntegrationControl && (
                      <SlackIntegrationField
                        securityLevel={values.security.level}
                      />
                    )}
                  </div>
                  <WelcomeMatField
                    allowWelcomeMatConfigChange
                    handlePreviewCoverPage={setShowFormPreviewModal.setTrue}
                  />
                  <div className="flex flex-col pl-2">
                    <Field
                      as={Checkbox}
                      name="autoSyncUpdates"
                      type="checkbox"
                      label={
                        <TextBase
                          color="text-ds-natural-600"
                          label="Auto-sync updates"
                        />
                      }
                      className="p-2"
                    />

                    <div className="pl-6">
                      <TextCaption label="Automatically sync new tour edits with this tour link" />
                    </div>
                  </div>
                </AdvancedSettings>
              </div>

              <div className="flex flex-col gap-6">
                {isLoading ? (
                  <div className="flex justify-center items-center">
                    <LoadingDots />
                  </div>
                ) : (
                  <div className="flex justify-end gap-2">
                    <Button
                      secondary
                      label="Cancel"
                      onClick={() => {
                        onClose();
                      }}
                    />
                    <SubmitButton
                      render={({ isValid }) => {
                        return (
                          <Button
                            disabled={!isValid}
                            label={yesLabel}
                            submit
                          />
                        );
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
            {user && organization && (
              <FormPreviewModal
                welcomeMatConfig={values.cover}
                open={showFormPreviewModal}
                assetType="tour"
                organizationName={organization.organizationName}
                presenterName={user.name}
                organizationLogo={organization.logo}
                emailDomain={values.security.emailDomain}
                securityLevel={values.security.level}
                onClose={setShowFormPreviewModal.setFalse}
                welcomeMatTitle={values.welcomeMatTitle || 'Title appears here'}
              />
            )}
          </Form>
        )}
      </Formik>
    </Modal>
  );
};
